import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  useMediaQuery,
  Popover,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Tune as FilterIcon,
  LocalHospital as LabIcon,
  Science as ScienceIcon,
  Category as CategoryIcon,
  BloodtypeOutlined as PhlebotomyIcon,
  LocationOn as LocationIcon,
  MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import SwalWithA11yFix from '../../services/swalConfig';
import TestDetailModal from './TestDetailModal';
import CompareTestsModal from './CompareTestsModal';

/**
 * LabCatalogContent Component
 * A reusable component that displays the lab catalog with filters, search, and test details modal
 * Can be embedded anywhere - in pages or dashboards
 * 
 * Props:
 * - embedded (boolean): If true, hides the hero section and header for dashboard embedding
 * - showComparison (boolean): If true, allows test comparison (default: true)
 * - showSelectButton (boolean): If true, shows "Add" button instead of "Details" button
 * - onSelectTest (function): Callback when select button is clicked
 * - selectedTestIds (array): Array of test IDs already selected (highlights as "In Selection")
 */
const LabCatalogContent = ({ 
  embedded = false, 
  showComparison = true,
  showSelectButton = false,
  onSelectTest = null,
  selectedTestIds = []
}) => {
  // Progressive breakpoints for filter visibility
  const isAbove1200 = useMediaQuery('(min-width: 1201px)');
  const isBetween960and1200 = useMediaQuery('(min-width: 961px) and (max-width: 1200px)');
  const isBetween600and960 = useMediaQuery('(min-width: 601px) and (max-width: 960px)');
  const isBelow600 = useMediaQuery('(max-width: 600px)');
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [allTests, setAllTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  
  // Filter states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLabs, setSelectedLabs] = useState([]);
  const [selectedSampleTypes, setSelectedSampleTypes] = useState([]);
  const [selectedPhlebotomy, setSelectedPhlebotomy] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  
  // Available options
  const [categories, setCategories] = useState([]);
  const [labs, setLabs] = useState([]);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Responsive filters - Progressive hiding based on screen size
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const visibleFilters = isAbove1200
    ? ['category', 'lab', 'sampleType', 'phlebotomy', 'location']
    : isBetween960and1200
    ? ['category', 'lab', 'sampleType', 'phlebotomy']
    : isBetween600and960
    ? ['category', 'lab', 'sampleType']
    : isBelow600
    ? ['category', 'lab']
    : ['category', 'lab', 'sampleType', 'phlebotomy', 'location'];
  
  const hiddenFilters = isAbove1200
    ? []
    : isBetween960and1200
    ? ['location']
    : isBetween600and960
    ? ['phlebotomy', 'location']
    : isBelow600
    ? ['sampleType', 'phlebotomy', 'location']
    : [];
  
  const moreFiltersAnchor = useRef(null);
  const showMoreButton = hiddenFilters.length > 0;
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredTests.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTests = filteredTests.slice(startIndex, endIndex);

  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...allTests];

    // Search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (test) =>
          test.name?.toLowerCase().includes(query) ||
          test.description?.toLowerCase().includes(query) ||
          test.lab?.name?.toLowerCase().includes(query) ||
          test.category?.name?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((test) =>
        selectedCategories.includes(test.category?._id)
      );
    }

    // Lab filter
    if (selectedLabs.length > 0) {
      filtered = filtered.filter((test) =>
        selectedLabs.includes(test.lab?._id)
      );
    }

    // Sample type filter
    if (selectedSampleTypes.length > 0) {
      filtered = filtered.filter((test) =>
        selectedSampleTypes.includes(test.sampleType?._id)
      );
    }

    // Phlebotomy filter
    if (selectedPhlebotomy.length > 0) {
      filtered = filtered.filter((test) =>
        selectedPhlebotomy.includes(test.phlebotomy)
      );
    }

    // Location filter
    if (selectedLocations.length > 0) {
      filtered = filtered.filter((test) =>
        selectedLocations.includes(test.location?._id)
      );
    }

    setFilteredTests(filtered);
  }, [
    allTests,
    searchQuery,
    selectedCategories,
    selectedLabs,
    selectedSampleTypes,
    selectedPhlebotomy,
    selectedLocations,
  ]);

  // Apply filters
  useEffect(() => {
    applyFilters();
    setPage(1); // Reset to first page when filters change
  }, [applyFilters]);

  // Check for compare URL parameter and auto-open comparison modal
  useEffect(() => {
    if (!embedded) {
      const params = new URLSearchParams(window.location.search);
      const compareParam = params.get('compare');
      if (compareParam && allTests.length > 0 && !detailModalOpen) {
        setCompareModalOpen(true);
      }
    }
  }, [allTests, detailModalOpen, embedded]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [testsRes, categoriesRes, labsRes, sampleTypesRes, locationsRes] = await Promise.all([
        api.get('/tests'),
        api.get('/categories'),
        api.get('/labs'),
        api.get('/sampleTypes'),
        api.get('/locations'),
      ]);

      setAllTests(testsRes.data);
      setFilteredTests(testsRes.data);
      setCategories(categoriesRes.data);
      setLabs(labsRes.data);
      setSampleTypes(sampleTypesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load lab catalog',
        confirmButtonColor: '#1976d2',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (filterType) => {
    switch (filterType) {
      case 'category':
        setSelectedCategories([]);
        break;
      case 'lab':
        setSelectedLabs([]);
        break;
      case 'sampleType':
        setSelectedSampleTypes([]);
        break;
      case 'phlebotomy':
        setSelectedPhlebotomy([]);
        break;
      case 'location':
        setSelectedLocations([]);
        break;
      default:
        break;
    }
  };

  const getSampleTypeColor = (sampleTypeName) => {
    const name = sampleTypeName?.toLowerCase() || '';
    if (name.includes('stool')) return '#FF6B35';
    if (name.includes('serum')) return '#E63946';
    if (name.includes('blood')) return '#D62828';
    if (name.includes('saliva')) return '#00BBF9';
    if (name.includes('urine')) return '#F77F00';
    return '#6C757D';
  };

  const handleDetails = (test) => {
    setSelectedTest(test);
    setDetailModalOpen(true);
  };

  const handleCompareTests = () => {
    setDetailModalOpen(false);
    setCompareModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedTest(null);
  };

  const renderMultiSelectDropdown = (label, icon, value, onChange, options, filterType, idKey = '_id', nameKey = 'name') => (
    <FormControl 
      size="small"
      sx={{
        minWidth: 'auto',
        flexShrink: 0,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'white',
          borderRadius: '6px',
          height: 40,
          fontSize: '0.9rem',
          '&:hover': {
            backgroundColor: 'white',
            borderColor: '#d0d0d0',
          },
        },
        '& .MuiOutlinedInput-input': {
          py: '10px',
          px: '12px',
        },
      }}
    >
      <Select
        multiple
        displayEmpty
        value={value}
        onChange={onChange}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, whiteSpace: 'nowrap' }}>
                {icon}
                <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{label}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#999' }}>
                  All
                </Typography>
              </Box>
            );
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, whiteSpace: 'nowrap' }}>
              {icon}
              <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{label}</Typography>
              <Chip 
                label={selected.length} 
                size="small" 
                sx={{ 
                  height: 22, 
                  fontSize: '0.75rem',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontWeight: 600,
                }} 
              />
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
      >
        <MenuItem 
          value="reset" 
          onClick={() => handleReset(filterType)}
          sx={{ 
            color: '#1976d2', 
            fontWeight: 'bold',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          🗑️ Reset
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option[idKey]} value={option[idKey]}>
            <Checkbox checked={value.indexOf(option[idKey]) > -1} />
            <ListItemText primary={option[nameKey]} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderPhlebotomyDropdown = () => (
    <FormControl 
      size="small"
      sx={{
        minWidth: 'auto',
        flexShrink: 0,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'white',
          borderRadius: '6px',
          height: 40,
          fontSize: '0.9rem',
          '&:hover': {
            backgroundColor: 'white',
            borderColor: '#d0d0d0',
          },
        },
        '& .MuiOutlinedInput-input': {
          py: '10px',
          px: '12px',
        },
      }}
    >
      <Select
        multiple
        displayEmpty
        value={selectedPhlebotomy}
        onChange={(e) => {
          const value = e.target.value;
          if (value.includes('reset')) {
            handleReset('phlebotomy');
          } else {
            setSelectedPhlebotomy(value);
          }
        }}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, whiteSpace: 'nowrap' }}>
                <PhlebotomyIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Phlebotomy</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#999' }}>
                  All
                </Typography>
              </Box>
            );
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, whiteSpace: 'nowrap' }}>
              <PhlebotomyIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Phlebotomy</Typography>
              <Chip 
                label={selected.length} 
                size="small" 
                sx={{ 
                  height: 22, 
                  fontSize: '0.75rem',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontWeight: 600,
                }} 
              />
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
      >
        <MenuItem 
          value="reset" 
          onClick={() => handleReset('phlebotomy')}
          sx={{ 
            color: '#1976d2', 
            fontWeight: 'bold',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          🗑️ Reset
        </MenuItem>
        <MenuItem value="Required">
          <Checkbox checked={selectedPhlebotomy.indexOf('Required') > -1} />
          <ListItemText primary="Required" />
        </MenuItem>
        <MenuItem value="Not Required">
          <Checkbox checked={selectedPhlebotomy.indexOf('Not Required') > -1} />
          <ListItemText primary="Not Required" />
        </MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ backgroundColor: embedded ? 'transparent' : '#f5f7fa', pb: embedded ? 0 : 4 }}>
      {/* Search Bar */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          mb: 3,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search for tests, categories, biomarkers & more"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#1976d2', fontSize: 24 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small">
                  <FilterIcon sx={{ color: '#1976d2' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#1976d2',
                borderWidth: '2px',
              },
              '&:hover fieldset': {
                borderColor: '#1565c0',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
              padding: '8px 12px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#999',
              opacity: 0.7,
            },
          }}
        />
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.2,
          mb: 3,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {visibleFilters.includes('category') && renderMultiSelectDropdown(
          'Category',
          <CategoryIcon fontSize="small" />,
          selectedCategories,
          (e) => {
            const value = e.target.value;
            if (value.includes('reset')) {
              handleReset('category');
            } else {
              setSelectedCategories(value);
            }
          },
          categories,
          'category'
        )}

        {visibleFilters.includes('lab') && renderMultiSelectDropdown(
          'Lab Company',
          <LabIcon fontSize="small" />,
          selectedLabs,
          (e) => {
            const value = e.target.value;
            if (value.includes('reset')) {
              handleReset('lab');
            } else {
              setSelectedLabs(value);
            }
          },
          labs,
          'lab'
        )}

        {visibleFilters.includes('sampleType') && renderMultiSelectDropdown(
          'Sample Type',
          <ScienceIcon fontSize="small" />,
          selectedSampleTypes,
          (e) => {
            const value = e.target.value;
            if (value.includes('reset')) {
              handleReset('sampleType');
            } else {
              setSelectedSampleTypes(value);
            }
          },
          sampleTypes,
          'sampleType'
        )}

        {visibleFilters.includes('phlebotomy') && renderPhlebotomyDropdown()}

        {visibleFilters.includes('location') && renderMultiSelectDropdown(
          'Patient Location',
          <LocationIcon fontSize="small" />,
          selectedLocations,
          (e) => {
            const value = e.target.value;
            if (value.includes('reset')) {
              handleReset('location');
            } else {
              setSelectedLocations(value);
            }
          },
          locations,
          'location'
        )}

        {/* More Filters Button - Show when filters are hidden */}
        {showMoreButton && (
          <Button
            ref={moreFiltersAnchor}
            onClick={(e) => setShowMoreFilters(!showMoreFilters)}
            size="small"
            sx={{
              minWidth: 'auto',
              height: 40,
              px: 1.5,
              backgroundColor: showMoreFilters ? '#e3f2fd' : 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              whiteSpace: 'nowrap',
              textTransform: 'none',
              color: showMoreFilters ? '#1976d2' : '#666',
              fontWeight: 500,
              fontSize: '0.9rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: showMoreFilters ? '#e3f2fd' : '#fafafa',
                borderColor: '#d0d0d0',
              },
            }}
          >
            <MoreIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>More filters</Typography>
          </Button>
        )}

        {/* More Filters Popover - Show hidden filters */}
        {showMoreButton && (
          <Popover
            open={showMoreFilters}
            anchorEl={moreFiltersAnchor.current}
            onClose={() => setShowMoreFilters(false)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  mt: 1,
                },
              },
            }}
          >
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 320 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {hiddenFilters.length === 1 ? 'More Filters' : 'Additional Filters'}
              </Typography>
              
              {hiddenFilters.includes('sampleType') && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {renderMultiSelectDropdown(
                    'Sample Type',
                    <ScienceIcon fontSize="small" />,
                    selectedSampleTypes,
                    (e) => {
                      const value = e.target.value;
                      if (value.includes('reset')) {
                        handleReset('sampleType');
                      } else {
                        setSelectedSampleTypes(value);
                      }
                    },
                    sampleTypes,
                    'sampleType'
                  )}
                </Box>
              )}

              {hiddenFilters.includes('phlebotomy') && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {renderPhlebotomyDropdown()}
                </Box>
              )}

              {hiddenFilters.includes('location') && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {renderMultiSelectDropdown(
                    'Patient Location',
                    <LocationIcon fontSize="small" />,
                    selectedLocations,
                    (e) => {
                      const value = e.target.value;
                      if (value.includes('reset')) {
                        handleReset('location');
                      } else {
                        setSelectedLocations(value);
                      }
                    },
                    locations,
                    'location'
                  )}
                </Box>
              )}
            </Box>
          </Popover>
        )}
      </Box>

      {/* Results Table */}
      <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            border: '1px solid #e8e8e8',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e8e8e8' }}>
                <TableCell sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px', py: 2 }}>
                  LAB TEST
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px', py: 2 }}>
                  SAMPLE TYPE
                </TableCell>
                <TableCell sx={{ py: 2 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredTests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTests.map((test) => (
                  <TableRow
                    key={test._id}
                    hover
                    sx={{
                      borderBottom: '1px solid #e8e8e8',
                      '&:hover': {
                        backgroundColor: '#fafafa',
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      }
                    }}
                  >
                    <TableCell sx={{ py: 2.5, pr: 3 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#0066cc',
                            mb: 0.5,
                            fontSize: '0.95rem',
                          }}
                        >
                          {test.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#666',
                            fontSize: '0.85rem',
                          }}
                        >
                          {test.lab?.name || 'Unknown Lab'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Box>
                        <Chip
                          label={test.sampleType?.name?.toUpperCase() || 'N/A'}
                          size="small"
                          sx={{
                            backgroundColor: getSampleTypeColor(test.sampleType?.name),
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            mb: test.phlebotomy === 'Required' ? 0.5 : 0,
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                          }}
                        />
                        {test.phlebotomy === 'Required' && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: '#666',
                              fontSize: '0.75rem',
                              mt: 0.5,
                            }}
                          >
                            Phlebotomy required
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2.5 }}>
                      {showSelectButton ? (
                        selectedTestIds.includes(test._id) ? (
                          <Button
                            variant="contained"
                            size="small"
                            disabled
                            sx={{
                              backgroundColor: '#4caf50',
                              color: 'white',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.9rem',
                            }}
                          >
                            ✓ In Selection
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onSelectTest && onSelectTest(test)}
                            sx={{
                              color: '#1976d2',
                              borderColor: '#1976d2',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                borderColor: '#1565c0',
                              },
                            }}
                          >
                            Add
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleDetails(test)}
                          sx={{
                            color: '#1976d2',
                            borderColor: '#1976d2',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              borderColor: '#1565c0',
                            },
                          }}
                        >
                          Details
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

      {/* Pagination */}
      {!loading && filteredTests.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredTests.length)} of {filteredTests.length} tests
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.95rem',
              },
            }}
          />
        </Box>
      )}

      {/* Results Summary */}
      {!loading && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredTests.length)} of {filteredTests.length} tests
          </Typography>
          {filteredTests.length !== allTests.length && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
              (Filtered from {allTests.length} total tests)
            </Typography>
          )}
        </Box>
      )}

      {/* Test Detail Modal */}
      <TestDetailModal
        open={detailModalOpen}
        test={selectedTest}
        onClose={handleCloseDetailModal}
        onCompare={showComparison ? handleCompareTests : undefined}
      />

      {/* Compare Tests Modal */}
      {showComparison && (
        <CompareTestsModal
          open={compareModalOpen}
          onClose={(comparedTests) => {
            setCompareModalOpen(false);
            // Only reopen detail modal if the initial test is still in the comparison
            if (selectedTest && comparedTests && comparedTests.some(t => t._id === selectedTest._id)) {
              setDetailModalOpen(true);
            }
          }}
          allTests={allTests}
          onAddTest={handleDetails}
          initialTest={selectedTest}
        />
      )}
    </Box>
  );
};

export default LabCatalogContent;

